import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Check, Clock, Loader2, Play, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { useDecideCandidate, useDiscoveryRun, useDiscoveryRuns, useStartDiscovery } from '../hooks/useDiscovery'
import type { CandidateStatus, DiscoveryCandidate, DiscoveryTopic } from '../api/discoveryApi'

const num = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString())

const DESCRIPTION = (
  <>
    Where people search for art we barely carry, and external artists who could fill it. Demand is Search Console
    category searches (a topic plus an art word; title lookups, artist names, licensed IP and blog traffic left out)
    and sales capped per artist; supply is what we sell in the concept. Candidates come from Pictufy, scored for
    focus and quality, then judged on their best works against the NeuroGraph customer personas. A run only
    proposes: approving an artist is your call.
  </>
)

/** Demand (search impressions) against supply (works we sell), log scales. Bigger = bigger gap. */
function OpportunityMap({ topics, selected, onSelect }: { topics: DiscoveryTopic[]; selected?: string; onSelect: (u: string) => void }) {
  const W = 560
  const H = 300
  const pad = 36
  const lx = (v: number) => Math.log10(1 + v)
  const xs = topics.map((t) => lx(t.supply.onSale))
  const ys = topics.map((t) => lx(t.demand.impressions))
  const [x0, x1] = [0, Math.max(1, ...xs)]
  const [y0, y1] = [Math.min(...ys, 2), Math.max(...ys, 3)]
  const sx = (v: number) => pad + ((v - x0) / (x1 - x0 || 1)) * (W - 2 * pad)
  const sy = (v: number) => H - pad - ((v - y0) / (y1 - y0 || 1)) * (H - 2 * pad)
  const maxGap = Math.max(...topics.map((t) => t.gapScore), 1)
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-3xl rounded border bg-white">
      <rect x={pad} y={pad} width={(W - 2 * pad) / 2} height={(H - 2 * pad) / 2} className="fill-amber-50" />
      <text x={pad + 6} y={pad + 14} className="fill-amber-700 text-[10px]">high demand, little supply</text>
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} className="stroke-slate-300" />
      <line x1={pad} y1={pad} x2={pad} y2={H - pad} className="stroke-slate-300" />
      <text x={W / 2} y={H - 8} textAnchor="middle" className="fill-slate-500 text-[10px]">works we sell in the concept (log)</text>
      <text x={10} y={H / 2} transform={`rotate(-90 10 ${H / 2})`} textAnchor="middle" className="fill-slate-500 text-[10px]">
        category search impressions (log)
      </text>
      {topics.map((t, i) => (
        <g key={t.ucid} className="cursor-pointer" onClick={() => onSelect(t.ucid)}>
          <circle
            cx={sx(xs[i])}
            cy={sy(ys[i])}
            r={5 + 10 * (t.gapScore / maxGap)}
            className={t.ucid === selected ? 'fill-slate-900/80' : 'fill-sky-500/50 hover:fill-sky-600/70'}
          />
          <text x={sx(xs[i]) + 8 + 10 * (t.gapScore / maxGap)} y={sy(ys[i]) + 3} className="fill-slate-700 text-[10px]">
            {t.name}
          </text>
        </g>
      ))}
    </svg>
  )
}

function TopicRow({ t, active, onClick }: { t: DiscoveryTopic; active: boolean; onClick: () => void }) {
  const approved = t.candidates.filter((c) => c.status === 'approved').length
  return (
    <button
      onClick={onClick}
      className={`w-full rounded border px-3 py-2 text-left text-sm ${active ? 'border-slate-900 bg-slate-50' : 'hover:bg-slate-50'}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">
          {t.rank}. {t.name}
        </span>
        <span className="text-xs tabular-nums text-slate-500">gap {t.gapScore.toFixed(2)}</span>
      </div>
      <div className="text-xs text-slate-500">
        {num(t.demand.impressions)} impressions · position {t.demand.position == null ? '—' : t.demand.position.toFixed(0)} ·{' '}
        {num(t.supply.onSale)} ours · {t.candidates.length} artists{approved ? ` · ${approved} approved` : ''}
      </div>
    </button>
  )
}

const STATUS_STYLE: Record<CandidateStatus, string> = {
  proposed: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-100 text-emerald-800',
  skipped: 'bg-rose-100 text-rose-800',
  snoozed: 'bg-amber-100 text-amber-800',
}

function CandidateCard({ c }: { c: DiscoveryCandidate }) {
  const decide = useDecideCandidate()
  const k = c.components
  const flags = Object.entries(k.flags || {})
  const set = (status: CandidateStatus) => decide.mutate({ id: c.id, status })
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              {c.rank}. {c.artistName}
            </CardTitle>
            <CardDescription>
              {k.country || 'country unknown'} · {num(k.catalogue)} works on {c.provider} · {k.hits} in this topic · score{' '}
              {c.score.toFixed(2)}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[c.status]}`}>{c.status}</span>
            {k.amsArtistId && (
              <Link to={`/app/artists/${k.amsArtistId}`} className="text-xs text-sky-700 hover:underline">
                AMS artist {k.amsArtistId}
              </Link>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 overflow-x-auto">
          {c.works.slice(0, 8).map((w) =>
            w.image ? (
              <a key={w.externalId} href={w.image} target="_blank" rel="noreferrer" title={w.title || ''} className="shrink-0">
                <img src={w.image} alt={w.title || ''} className="h-28 w-auto rounded border object-cover" loading="lazy" />
              </a>
            ) : null,
          )}
        </div>
        <div className="grid gap-2 text-xs sm:grid-cols-2">
          <div className="space-y-1">
            <div className="text-slate-500">
              Persona fit {k.aestheticFit == null ? '—' : `${k.aestheticFit.toFixed(1)} / 10`} · quality {((k.quality || 0) * 5).toFixed(1)} / 5 ·
              recent {Math.round((k.recency || 0) * 100)}% · focus {(k.focus || 0).toFixed(2)}
            </div>
            {(k.personas || []).slice(0, 3).map((p) => (
              <div key={p.name} className="flex items-center gap-2">
                <span className="w-44 truncate">{p.name}</span>
                <span className="h-1.5 flex-1 rounded bg-slate-100">
                  <span className="block h-1.5 rounded bg-sky-500" style={{ width: `${p.score * 10}%` }} />
                </span>
                <span className="w-6 text-right tabular-nums">{p.score.toFixed(1)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1">
            {c.reason && <p className="text-slate-700">{c.reason}</p>}
            {flags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {flags.map(([f, n]) => (
                  <Badge key={f} variant="outline" className="text-[10px]">
                    {f.replace(/([A-Z])/g, ' $1').toLowerCase()} ×{n}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => set('approved')} disabled={decide.isPending || c.status === 'approved'}>
            <Check className="mr-1 h-4 w-4" /> Approve
          </Button>
          <Button size="sm" variant="outline" onClick={() => set('snoozed')} disabled={decide.isPending}>
            <Clock className="mr-1 h-4 w-4" /> Snooze
          </Button>
          <Button size="sm" variant="outline" onClick={() => set('skipped')} disabled={decide.isPending}>
            <X className="mr-1 h-4 w-4" /> Skip
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export function OpportunitiesPage() {
  const runs = useDiscoveryRuns()
  const { data, isLoading, error } = useDiscoveryRun('latest')
  const start = useStartDiscovery()
  const [selected, setSelected] = useState<string>()
  const open = (runs.data?.data ?? []).find((r) => r.status === 'queued' || r.status === 'running')
  const run = data?.data
  // The newest run failed after the one shown (or with none shown): say where and why, not just "no run yet".
  const newest = runs.data?.data?.[0]
  const failed = newest?.status === 'failed' && (!run || newest.id > run.id) ? newest : undefined
  const topics = useMemo(() => run?.topics ?? [], [run])
  const topic = topics.find((t) => t.ucid === selected) ?? topics[0]

  return (
    <KnowledgeFrame title="Opportunities" description={DESCRIPTION}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {run && (
          <span className="text-slate-500">
            Run {run.id}, finished {run.finishedAt ? formatDistanceToNow(new Date(run.finishedAt), { addSuffix: true }) : '—'}
          </span>
        )}
        {open ? (
          <span className="flex items-center gap-2 text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Run {open.id} at “{open.stage}”…
          </span>
        ) : (
          <Button size="sm" variant="outline" onClick={() => start.mutate({})} disabled={start.isPending}>
            <Play className="mr-1 h-4 w-4" /> Run discovery now
          </Button>
        )}
      </div>

      {failed && (
        <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Run {failed.id} failed at “{failed.stage}”
          {failed.finishedAt ? ` ${formatDistanceToNow(new Date(failed.finishedAt), { addSuffix: true })}` : ''}:{' '}
          <span className="font-mono text-xs">{failed.error || 'no error recorded'}</span>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : error || !run ? (
        <Card>
          <CardContent className="p-6 text-sm text-slate-600">No finished discovery run yet. Start one above; the weekly run is Mondays 06:00 UTC.</CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr]">
          <div className="space-y-2">
            {topics.map((t) => (
              <TopicRow key={t.ucid} t={t} active={t.ucid === topic?.ucid} onClick={() => setSelected(t.ucid)} />
            ))}
          </div>
          <div className="space-y-4">
            <OpportunityMap topics={topics} selected={topic?.ucid} onSelect={setSelected} />
            {topic && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-lg font-semibold">{topic.name}</h2>
                  <p className="text-xs text-slate-500">
                    {num(topic.demand.impressions)} category-search impressions, {num(topic.demand.clicks)} clicks, average position{' '}
                    {topic.demand.position == null ? '—' : topic.demand.position.toFixed(1)}, trend ×{topic.demand.trend.toFixed(2)} ·{' '}
                    {num(topic.demand.units)} units sold (capped per artist) · {num(topic.supply.onSale)} works on sale · searched Pictufy for{' '}
                    {topic.searchTerms.map((s) => `“${s}”`).join(', ')}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Top searches: {topic.demand.topQueries.slice(0, 6).map((q) => `${q.q} (${num(q.impressions)})`).join(' · ')}
                  </p>
                </div>
                {topic.candidates.length === 0 ? (
                  <p className="text-sm text-slate-500">No candidates passed the judge for this topic.</p>
                ) : (
                  topic.candidates.map((c) => <CandidateCard key={c.id} c={c} />)
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </KnowledgeFrame>
  )
}
